import { useCallback, useEffect, useRef, useState, useId } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useMatch } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import OrgIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { organizationService, type Organization } from '@services/organizationService';
import { useActiveOrgStore, useAuthStore, type ActiveOrgSummary } from '@store/index';
import { visuallyHidden } from '@mui/utils';

// Organization list item as returned by GET /v1/organizations — the backend
// decorates each org with the membership role of the calling user.
type MembershipOrg = Organization & { role?: string; is_default?: boolean };

function OrgSwitcher() {
  const { t } = useTranslation();
  const theme = useTheme();
  // WCAG 1.4.10 (reflow). At 320px the app bar has a content budget of 288px,
  // and the fixed furniture — nav toggle 44, language 44, avatar 52, gaps 24 —
  // already claims 164 of it. A switcher showing "Acme Corp" plus a chevron is
  // 140 more, which overflowed the document to 400px.
  //
  // This was NOT introduced by REV-676: the app bar has always overflowed at
  // 320px whenever this control rendered, verified against develop with two
  // organizations. The reflow gate never caught it because it mocks exactly ONE
  // organization, and the old `orgs.length <= 1` guard hid the switcher in
  // precisely that case — the gate tested the one configuration where the bug
  // could not appear. Rendering at every count is what exposed it.
  //
  // Narrow screens get the icon alone. The label stays in the DOM, visually
  // hidden, so the button keeps its accessible name; with no visible text,
  // WCAG 2.5.3 (Label in Name) does not apply, so REV-537's fix is not undone.
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const { activeOrg, setActiveOrg } = useActiveOrgStore();
  const routeMatch = useMatch('/orgs/:slug/*');

  const [orgs, setOrgs] = useState<MembershipOrg[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const page = await organizationService.list({ limit: 100 });
      setOrgs((page.items ?? []) as MembershipOrg[]);
    } catch {
      // Swallow the error: the switcher is an optional nav aid, and pages
      // that actually consume org data own their own error UX.
      //
      // Leave `orgs` as it was. On the first load it is still the initial
      // empty list, so there is nothing to clear; on a background reload (the
      // unknown-slug retry below) it holds a list that DID load, and replacing
      // it with [] told a user who has organizations to "Create your first"
      // (REV-939 review).
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !loaded && !loading) {
      void loadOrgs();
    }
  }, [isAuthenticated, loaded, loading, loadOrgs]);

  // Keep the active-org store in sync with /orgs/:slug routes, so that
  // arriving at a client org page via a direct link or breadcrumb doesn't
  // leave the switcher pointing at a stale selection.
  //
  // REV-939: the list is loaded once, so an org created after that is not in
  // it — and its page kept the previous org active, which also left the
  // breadcrumb showing the slug. A slug the list lacks triggers ONE reload;
  // remembering which slug was retried stops a slug the user cannot see
  // (a mistyped link, a revoked membership) from reloading forever.
  const retriedSlug = useRef<string | null>(null);
  useEffect(() => {
    const slug = routeMatch?.params.slug;
    if (!slug || !loaded || loading) return;
    const onRoute = orgs.find((o) => o.slug === slug);
    if (onRoute) {
      if (onRoute.id !== activeOrg?.id) {
        setActiveOrg({
          id: onRoute.id,
          slug: onRoute.slug,
          name: onRoute.name,
          role: onRoute.role,
          locale: onRoute.locale ?? null,
        });
      }
    } else if (retriedSlug.current !== slug) {
      retriedSlug.current = slug;
      void loadOrgs();
    }
  }, [routeMatch, orgs, loaded, loading, loadOrgs, activeOrg?.id, setActiveOrg]);

  // If no active org but orgs are loaded, adopt the first (or is_default one)
  // so consumers can read activeOrg without having to special-case the empty
  // state. Do not navigate — the user may be on a page that has nothing to
  // do with orgs (profile, settings, etc.).
  useEffect(() => {
    if (!activeOrg && orgs.length > 0) {
      const preferred = orgs.find((o) => o.is_default) ?? orgs[0];
      if (preferred) {
        setActiveOrg({
          id: preferred.id,
          slug: preferred.slug,
          name: preferred.name,
          role: preferred.role,
          locale: preferred.locale ?? null,
        });
      }
    }
  }, [activeOrg, orgs, setActiveOrg]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (!loaded && !loading) void loadOrgs();
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (org: MembershipOrg) => {
    const summary: ActiveOrgSummary = {
      id: org.id,
      slug: org.slug,
      name: org.name,
      role: org.role,
      locale: org.locale ?? null,
    };
    setActiveOrg(summary);
    handleClose();
    // Navigate only if we're not already on a page scoped to this org.
    const alreadyHere =
      location.pathname === `/orgs/${org.slug}` ||
      location.pathname.startsWith(`/orgs/${org.slug}/`);
    if (!alreadyHere) {
      void navigate(`/orgs/${org.slug}`);
    }
  };

  // REV-687: this one item carries two labels. With no organizations it reads
  // "Create your first organization", and sending that to /orgs to stop —
  // leaving the user to find and press Create — is the same "takes you NEAR
  // the action" defect REV-687 fixes on the dashboard button. Worse, without
  // this the two paths that both say "create" would behave differently.
  //
  // With organizations it reads "Manage organizations", where landing on the
  // list is exactly right and a dialog would be in the way.
  //
  // REV-732: a destination rather than a handler, so the item can be a link.
  //
  // KEEP onClick={handleClose} beside it. I removed it first, having measured
  // on UserMenu that <Menu onClose> fires on item click anyway — and that
  // generalisation was wrong. Here the anchor navigates without the Menu ever
  // closing, which leaves MUI's modal `aria-hidden="true"` on #root: the new
  // page renders, 6 buttons exist in the DOM, and NONE of them are in the
  // accessibility tree. An e2e caught it; no unit test could.
  //
  // That is a total accessibility failure in an accessibility product, so the
  // close is not optional here even though it looked redundant elsewhere.
  const manageHref = orgs.length === 0 ? '/orgs?create=1' : '/orgs';

  // useId rather than a fixed string: a hardcoded DOM id becomes a duplicate
  // the moment this renders twice (a second header variant, an embed), and a
  // duplicated aria-describedby target is ambiguous.
  //
  // Declared ABOVE the early returns below — a hook called after a conditional
  // return changes hook order between renders, which React forbids.
  const descriptionId = useId();

  // This component is NOT only a switcher: the "Manage organizations" item at
  // the bottom of its menu is the ONLY link into /orgs anywhere in the app,
  // because REV-589 removed the Organizations entry from the nav drawer on the
  // explicit grounds that "the header menu already carries Manage
  // Organizations". So any condition that hides this control also deletes the
  // only route to creating, renaming or leaving an organization.
  //
  // REV-676 was that bug twice over. The guard was `orgs.length <= 1`:
  //
  //   0 orgs — a brand-new account. The home page told them to pick from a
  //            switcher that was not rendered, and every other nav entry was
  //            disabled for want of an active org. A dead end from signup.
  //   1 org  — reported after the first fix: create an organization, click to
  //            another page, and the switcher vanishes. No way to create a
  //            SECOND one. The dead end had simply moved one org later.
  //
  // "Nothing to switch between" was true and beside the point. The menu still
  // carries the only management action, so it is rendered at every count.
  if (!isAuthenticated) return null;

  const label = activeOrg?.name ?? t('orgSwitcher.select');

  return (
    <>
      <Button
        onClick={handleOpen}
        aria-haspopup="true"
        // REV-525: present as "false" when closed, never omitted (ARIA-04).
        aria-expanded={open}
        // REV-537 (STRUCTURE-23): the button previously carried BOTH an
        // aria-label and visible text. Two competing labelling strategies —
        // the aria-label silently overrode the visible name, which is the
        // pattern WCAG 2.5.3 (Label in Name) exists to discourage and what
        // voice-control users trip over ("click Acme" not matching a name
        // beginning "Active organization:").
        //
        // Correct pattern: the accessible NAME comes from the visible text,
        // and the supplementary context moves to a DESCRIPTION. Screen-reader
        // users still hear "Acme, button, has popup, active organization,
        // click to switch"; voice users get a name that matches what they see.
        aria-describedby={descriptionId}
        startIcon={<OrgIcon />}
        endIcon={compact ? undefined : <ExpandMoreIcon />}
        color="inherit"
        sx={{
          textTransform: 'none',
          maxWidth: { xs: 'none', sm: 220 },
          // 44px floor on the compact (icon-only) form: the platform touch
          // target minimum. MUI's default Button padding leaves it at 40.
          minWidth: { xs: 44, sm: 0 },
          justifyContent: 'flex-start',
        }}
      >
        <Box
          component="span"
          sx={
            compact
              ? visuallyHidden
              : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
          }
        >
          {label}
        </Box>
      </Button>
      <Box component="span" id={descriptionId} sx={visuallyHidden}>
        {t('orgSwitcher.description')}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // REV-525 (ARIA-04): name the role="menu" itself. MUI puts a Menu-level
        // aria-label on the popover paper, not the MenuList that carries the
        // role, so it must go through MenuListProps.
        MenuListProps={{ 'aria-label': t('orgSwitcher.heading') }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { elevation: 3, sx: { minWidth: 280, mt: 1 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {t('orgSwitcher.heading')}
          </Typography>
        </Box>
        <Divider />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} aria-label={t('common.loading')} />
          </Box>
        )}

        {!loading && orgs.length === 0 && (
          // Informational text, NOT a disabled MenuItem. A disabled control
          // cannot explain itself and cannot be clicked to discover what is
          // wrong, which is the platform form rule — and putting that pattern
          // on the one screen a new user cannot get past is the worst place
          // for it. The actionable way out is the item below.
          <Box sx={{ px: 2, py: 1.5, maxWidth: 320 }}>
            <Typography variant="body2" color="text.secondary">
              {t('orgSwitcher.empty')}
            </Typography>
          </Box>
        )}

        {!loading &&
          orgs.map((org) => {
            const isActive = org.id === activeOrg?.id;
            return (
              <MenuItem
                key={org.id}
                onClick={() => handleSelect(org)}
                selected={isActive}
                aria-current={isActive ? 'true' : undefined}
              >
                <ListItemText
                  primary={org.name}
                  secondary={org.slug}
                  primaryTypographyProps={{ fontWeight: isActive ? 600 : 400 }}
                />
                {org.role && (
                  <Chip
                    size="small"
                    label={t(`organizations.roles.${org.role}`, { defaultValue: org.role })}
                    sx={{ ml: 2 }}
                  />
                )}
              </MenuItem>
            );
          })}

        <Divider />
        <MenuItem component={RouterLink} to={manageHref} onClick={handleClose}>
          <ListItemText
            primary={orgs.length === 0 ? t('orgSwitcher.createFirst') : t('orgSwitcher.manage')}
          />
        </MenuItem>
      </Menu>
    </>
  );
}

export default OrgSwitcher;
